/**
 * Small, token-aware form primitives. Kept inline-styled to avoid Tailwind
 * tree-shaking surprises until we standardize on theme classes.
 *
 * TextInput and Textarea use forwardRef so callers can drive focus + cursor
 * position (needed for the merge-field "insert at cursor" UX in Templates).
 */
import { forwardRef } from 'react';
import type {
  ReactNode,
  InputHTMLAttributes,
  ButtonHTMLAttributes,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

export function FieldLabel({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return (
    <label
      htmlFor={htmlFor}
      style={{
        display: 'block',
        fontSize: 13,
        fontWeight: 500,
        color: 'var(--text-heading)',
        marginBottom: 6,
      }}
    >
      {children}
    </label>
  );
}

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function TextInput(props, ref) {
    return (
      <input
        {...props}
        ref={ref}
        style={{
          width: '100%',
          padding: '10px 12px',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          color: 'var(--text-body)',
          fontSize: 14,
          fontFamily: 'inherit',
          outline: 'none',
          ...props.style,
        }}
        // Style accents on focus/blur, but defensively null-check
        // e.currentTarget — Daniel hit a Cannot-read-properties-of-null
        // crash on a controlled datetime-local input that we believe was
        // a React 19 reconciliation race; guarding here means a stray
        // null event target degrades to "no focus halo" rather than a
        // full editor crash.
        onFocus={(e) => {
          if (e.currentTarget) {
            e.currentTarget.style.borderColor = 'var(--border-focus)';
            e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-primary-soft)';
          }
          props.onFocus?.(e);
        }}
        onBlur={(e) => {
          if (e.currentTarget) {
            e.currentTarget.style.borderColor = 'var(--border)';
            e.currentTarget.style.boxShadow = 'none';
          }
          props.onBlur?.(e);
        }}
      />
    );
  },
);

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger';
}

export function Button({ variant = 'primary', style, ...props }: ButtonProps) {
  const base = {
    primary: {
      background: 'var(--brand-primary)',
      color: 'var(--text-on-brand)',
      border: 'none',
    },
    secondary: {
      background: 'var(--bg-surface)',
      color: 'var(--text-heading)',
      border: '1px solid var(--border-strong)',
    },
    danger: {
      background: 'var(--danger)',
      color: '#fff',
      border: 'none',
    },
  }[variant];
  return (
    <button
      {...props}
      style={{
        padding: '10px 18px',
        borderRadius: 'var(--radius)',
        fontSize: 14,
        fontWeight: 500,
        cursor: props.disabled ? 'not-allowed' : 'pointer',
        opacity: props.disabled ? 0.6 : 1,
        ...base,
        ...style,
      }}
    />
  );
}

export function SelectInput({ children, style, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      style={{
        width: '100%',
        padding: '10px 12px',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        color: 'var(--text-body)',
        fontSize: 14,
        fontFamily: 'inherit',
        outline: 'none',
        cursor: 'pointer',
        ...style,
      }}
    >
      {children}
    </select>
  );
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ style, ...props }, ref) {
    return (
      <textarea
        {...props}
        ref={ref}
        style={{
          width: '100%',
          padding: '10px 12px',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          color: 'var(--text-body)',
          fontSize: 14,
          fontFamily: 'inherit',
          outline: 'none',
          resize: 'vertical',
          minHeight: 80,
          ...style,
        }}
      />
    );
  },
);

export function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      style={{
        background: 'var(--danger-soft)',
        color: 'var(--danger)',
        padding: '10px 12px',
        borderRadius: 'var(--radius)',
        fontSize: 13,
        marginBottom: 12,
      }}
    >
      {message}
    </div>
  );
}

export function AuthCard({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <div
      style={{
        maxWidth: 420,
        margin: '0 auto',
        padding: '64px 24px',
      }}
    >
      <div
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          padding: '32px 28px',
        }}
      >
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 24,
            fontWeight: 600,
            color: 'var(--text-heading)',
            margin: 0,
            letterSpacing: '-0.01em',
          }}
        >
          {title}
        </h1>
        {subtitle && (
          <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: '8px 0 24px' }}>{subtitle}</p>
        )}
        <div style={{ marginTop: subtitle ? 0 : 24 }}>{children}</div>
      </div>
    </div>
  );
}
